import {
  Alert,
  Button,
  PasswordInput,
  Stack,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  AlertCircle,
  ArrowLeft,
} from 'lucide-react'
import { useEffect } from 'react'
import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom'

import { isJWTFresh } from '@common/utils/validation/authValidators'
import { useAuthStore } from '@store/auth.store'
import { AuthShell } from './AuthShell'
import {
  getAuthPasswordValidationError,
  getConfirmPasswordError,
  PasswordRulesMessage,
} from './authFormHelpers'

interface ResetPasswordFormValues {
  password: string
  confirmPassword: string
}

const ResetPassword = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const resetPassword = useAuthStore((state) => state.resetPassword)
  const resettingPassword = useAuthStore((state) => state.resettingPassword)
  const passwordReset = useAuthStore((state) => state.passwordReset)
  const resetPasswordError = useAuthStore((state) => state.resetPasswordError)
  const resetAuthFormState = useAuthStore((state) => state.resetAuthFormState)

  const form = useForm<ResetPasswordFormValues>({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validateInputOnBlur: true,
    validate: {
      password: getAuthPasswordValidationError,
      confirmPassword: (value, values) =>
        getConfirmPasswordError(values.password, value),
    },
  })

  const isExpired = token ? !isJWTFresh(token) : true
  const showErrorMessage = isExpired || resetPasswordError

  useEffect(() => {
    resetAuthFormState()
  }, [resetAuthFormState])

  useEffect(() => {
    if (passwordReset) {
      navigate('/', { replace: true })
    }
  }, [navigate, passwordReset])

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    if (!token || isExpired) {
      return
    }

    await resetPassword(values.password, values.confirmPassword, token)
  }

  if (!token) {
    return (
      <AuthShell title={'Reset Password'}>
        <Alert
          color={'red'}
          icon={<AlertCircle size={16} />}
        >
          Invalid token
        </Alert>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={'Reset Password'}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={'md'}>
          <PasswordInput
            label={'New Password'}
            placeholder={'Enter new password'}
            autoComplete={'new-password'}
            disabled={isExpired}
            required
            {...form.getInputProps('password')}
          />
          <PasswordRulesMessage password={form.values.password} />
          <PasswordInput
            label={'Confirm New Password'}
            placeholder={'Enter new password again'}
            autoComplete={'new-password'}
            disabled={isExpired}
            required
            {...form.getInputProps('confirmPassword')}
          />
          <Button
            type={'submit'}
            loading={resettingPassword}
            disabled={
              isExpired ||
              passwordReset ||
              !form.values.password ||
              !form.values.confirmPassword
            }
          >
            Reset Password
          </Button>
          {showErrorMessage && (
            <Alert
              color={'red'}
              icon={<AlertCircle size={16} />}
            >
              {isExpired
                ? 'Reset password link had expired, please request a new link from the forgot password page'
                : 'An Error occurred while resetting your password. Please try again...'}
            </Alert>
          )}
          <Button
            component={Link}
            to={'/'}
            variant={'default'}
            leftSection={<ArrowLeft size={16} />}
          >
            Back to Login
          </Button>
        </Stack>
      </form>
    </AuthShell>
  )
}

export default ResetPassword
